import clsx from 'clsx'
import { html, unsafeCSS } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { DdsElement } from '@/internal/dds-hu-element'

import DapDSBreadcrumbItem from '../breadcrumb-item/breadcrumb-item.component'
import styles from './breadcrumb.scss?inline'

/**
 * `dap-ds-breadcrumb`
 * @summary A breadcrumb is a secondary navigation scheme that reveals the user's location in a website or Web application.
 * @element dap-ds-breadcrumb
 * @title - Breadcrumb
 *
 * @slot - The content of the breadcrumb.
 * @slot separator - The separator between breadcrumb items. Default is '/'.
 *
 * @csspart base - The main breadcrumb container.
 * @csspart separator - The separator of the breadcrumb.
 *
 * @cssproperty --dds-breadcrumb-width - The width of the breadcrumb container (default: 100%)
 * @cssproperty --dds-breadcrumb-overflow-x - The horizontal overflow behavior of the breadcrumb (default: auto)
 * @cssproperty --dds-breadcrumb-transition - The transition property for the breadcrumb (default: all 0.2s ease-in-out)
 * @cssproperty --dds-breadcrumb-list-display - The display property of the breadcrumb list (default: flex)
 * @cssproperty --dds-breadcrumb-list-flex-wrap - The flex-wrap property of the breadcrumb list (default: nowrap)
 * @cssproperty --dds-breadcrumb-list-align-items - The align-items property of the breadcrumb list (default: center)
 * @cssproperty --dds-breadcrumb-list-min-width - The minimum width of the breadcrumb list (default: max-content)
 */
@customElement('dap-ds-breadcrumb')
export default class DapDSBreadcrumb extends DdsElement {
  static readonly styles = unsafeCSS(styles)
  /** @ignore */
  @query('slot') defaultSlot!: HTMLSlotElement
  /** @ignore */
  @query('slot[name="separator"]') separatorSlot!: HTMLSlotElement

  /* Variant of the breadcrumb
   * @type {'normal' | 'inverted'}
   */
  @property({ type: String }) variant = 'normal'

  /** Mobile version of the breadcrumb */
  @property({ type: Boolean }) mobile = false

  /** The aria-labelledby of the breadcrumb */
  @property({ type: String, attribute: 'aria-labelledby' })
  ariaLabelledBy?: string

  private _cachedItems: DapDSBreadcrumbItem[] = []
  private _debounceTimeout?: number

  private cloneSeparator() {
    const separator = this.separatorSlot.assignedElements({
      flatten: true,
    })[0] as HTMLElement

    const clone = separator.cloneNode(true) as HTMLElement
    clone.slot = 'separator'

    return clone
  }

  private handleSlotChange(): void {
    if (this._debounceTimeout) {
      clearTimeout(this._debounceTimeout)
    }

    this._debounceTimeout = window.setTimeout(() => {
      this._updateBreadcrumbItems()
    }, 16) // One frame delay for batching
  }

  private _updateBreadcrumbItems(): void {
    const items = Array.from(
      this.defaultSlot.assignedElements({ flatten: true }),
    ).filter(
      (el): el is DapDSBreadcrumbItem => el instanceof DapDSBreadcrumbItem,
    )

    // Check if items have actually changed to avoid unnecessary work
    const itemsChanged =
      items.length !== this._cachedItems.length ||
      items.some((item, index) => item !== this._cachedItems[index])

    if (!itemsChanged) return

    this._cachedItems = items

    items.forEach((item, index) => {
      const isLast = index === items.length - 1

      if (this.mobile && index !== 0 && !isLast) {
        item.style.display = 'none'
        return
      } else {
        item.style.display = ''
      }

      item.toggleAttribute('inverted', this.variant === 'inverted')
      item.variant = this.variant
      if (isLast) {
        item.setAttribute('aria-current', 'page')
      } else {
        item.removeAttribute('aria-current')
      }
      item.toggleAttribute('disabled', isLast)

      const separator = item.querySelector('[slot="separator"]')
      if (!isLast && !separator) {
        item.append(this.cloneSeparator())
      }
    })
  }

  render() {
    return html`
      <nav
        part="base"
        aria-label="${ifDefined(this.ariaLabel) || ''}"
        aria-labelledby="${ifDefined(this.ariaLabelledBy) || ''}"
        class="${clsx('breadcrumb')}">
        <ol class="breadcrumb__list" part="list">
          <slot @slotchange="${this.handleSlotChange}"></slot>
        </ol>
      </nav>
      <span hidden aria-hidden="true">
        <slot name="separator"> <span>/</span> </slot>
      </span>
    `
  }
}
